import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import styles from './SalesManagerDashboardView.module.css';
import AppLayout from '../components/AppLayout';
import { getSalesDashboard } from '../api/salesReportsApi';
import { getProducts } from '../api/productApi';
import { getUsers } from '../api/api';
import { getAllDeals, getCustomers } from '../api/customerApi';
import { approveDiscount, getAllSalesRequests } from '../api/salesRequestApi';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

const translations = {
  en: {
    pageTitle: 'Sales Manager Dashboard',
    subtitle: 'Sales operations, approvals, deals, customers, reports, and inventory.',
    homepage: 'Homepage',
    logout: 'Logout',
    arabic: 'عربي',
    english: 'English',
    loading: 'Loading dashboard...',
    failedLoad: 'Failed to load sales manager dashboard.',
    discountUpdated: 'Discount decision saved.',
    failedDiscountUpdate: 'Failed to update discount decision.',
    period: 'Period',
    today: 'Today',
    week: 'This week',
    month: 'This month',
    year: 'This year',
    custom: 'Custom',
    startDate: 'Start date',
    endDate: 'End date',
    salesperson: 'Salesperson',
    allSalespeople: 'All salespeople',
    product: 'Product',
    allProducts: 'All products',
    customerSearch: 'Customer search',
    customerSearchPlaceholder: 'Name, ID, phone, organization, area',
    direction: 'Direction',
    allDirections: 'Inbound + outbound',
    inbound: 'Inbound',
    outbound: 'Outbound',
    requestStatus: 'Request status',
    allStatuses: 'All statuses',
    paymentStatus: 'Payment status',
    allPaymentStatuses: 'All payment statuses',
    clearFilters: 'Clear filters',
    quickActions: 'Quick actions',
    createRequest: 'Create request',
    customersPage: 'Customers',
    requestsPage: 'Requests',
    dealsPage: 'Invoices',
    inventoryPage: 'Inventory',
    usersPage: 'Sales team',
    totalNet: 'Net sales',
    companySales: 'Company sales',
    companySalesText: 'Sales value, quantities, collected income, and outstanding balances for the selected filters.',
    calculationsTitle: 'How these numbers are calculated',
    grossFormula: 'Gross sales = selected request item totals before approved discounts.',
    netFormula: 'Net sales = gross sales minus approved discounts only.',
    collectedFormula: 'Collected income = paid amounts recorded on the filtered invoices.',
    outstandingFormula: 'Outstanding = invoice amount due minus paid amount.',
    activityFormula: 'Customer status = active when the customer has at least one request in the last 6 months.',
    grossSales: 'Gross sales',
    collected: 'Collected income',
    totalQuantity: 'Quantity',
    requestCount: 'Requests',
    pendingDiscounts: 'Pending discounts',
    overdueInvoices: 'Overdue invoices',
    outstanding: 'Outstanding',
    activeCustomers: 'Customers',
    activeSalespeople: 'Sales users',
    discountQueue: 'Discount Queue',
    dealsInvoices: 'Deals / Invoices',
    customers: 'Customers',
    teamReports: 'Team Reports',
    productReports: 'Product Reports',
    inventory: 'Inventory',
    noData: 'No data for the selected filters.',
    customer: 'Customer',
    customerId: 'Customer ID',
    organization: 'Organization',
    phone: 'Phone',
    area: 'Area',
    assignedTo: 'Assigned to',
    action: 'Action',
    date: 'Date',
    request: 'Request',
    subtotal: 'Subtotal',
    requestedDiscount: 'Requested discount',
    discountAmount: 'Discount amount',
    totalIfApproved: 'Total if approved',
    currentTotal: 'Current total',
    approve: 'Approve',
    reject: 'Reject',
    pending: 'Pending',
    accepted: 'Accepted',
    rejected: 'Rejected',
    held: 'Held',
    cancelled: 'Cancelled',
    dateChanged: 'Date changed',
    notRequested: 'No discount',
    approved: 'Approved',
    cash: 'Cash',
    postpaid: 'Postpaid',
    unpaid: 'Unpaid',
    partiallyPaid: 'Partially paid',
    paid: 'Paid',
    salesUser: 'Sales user',
    dealDate: 'Deal date',
    products: 'Products',
    quantity: 'Qty',
    beforeTax: 'Before tax',
    tax: 'Tax',
    withTax: 'With tax',
    taxMode: 'Tax mode',
    paidAmount: 'Paid',
    remaining: 'Remaining',
    dueDate: 'Due date',
    view: 'View',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    sort: 'Sort',
    color: 'Color',
    paymentHistory: 'Payment history',
    noPayments: 'No payments recorded.',
    method: 'Method',
    notes: 'Notes',
    requests: 'Requests',
    fulfilled: 'Fulfilled',
    discount: 'Discount',
    customersServed: 'Customers',
    lastActivity: 'Last activity',
    sku: 'SKU',
    salespeople: 'Salespeople',
    avgQty: 'Avg qty',
    stock: 'Stock',
    price: 'Price',
    totalStock: 'Total stock',
    inventoryValue: 'Inventory value',
    status: 'Status',
    viewProfile: 'View profile',
    activeInPeriod: 'Active',
    inactiveInPeriod: 'Inactive',
    unassigned: 'Unassigned',
    notAvailable: '-',
  },
  ar: {
    pageTitle: 'لوحة مدير المبيعات',
    subtitle: 'عمليات المبيعات والاعتمادات والصفقات والعملاء والتقارير والمخزون.',
    homepage: 'الصفحة الرئيسية',
    logout: 'تسجيل الخروج',
    arabic: 'عربي',
    english: 'English',
    loading: 'جار تحميل اللوحة...',
    failedLoad: 'فشل تحميل لوحة مدير المبيعات.',
    discountUpdated: 'تم حفظ قرار الخصم.',
    failedDiscountUpdate: 'فشل تحديث قرار الخصم.',
    period: 'الفترة',
    today: 'اليوم',
    week: 'هذا الأسبوع',
    month: 'هذا الشهر',
    year: 'هذه السنة',
    custom: 'مخصص',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية',
    salesperson: 'مندوب المبيعات',
    allSalespeople: 'كل المندوبين',
    product: 'المنتج',
    allProducts: 'كل المنتجات',
    customerSearch: 'بحث العميل',
    customerSearchPlaceholder: 'الاسم أو الرقم أو الهاتف أو المؤسسة أو المنطقة',
    direction: 'الاتجاه',
    allDirections: 'وارد + صادر',
    inbound: 'وارد',
    outbound: 'صادر',
    requestStatus: 'حالة الطلب',
    allStatuses: 'كل الحالات',
    paymentStatus: 'حالة الدفع',
    allPaymentStatuses: 'كل حالات الدفع',
    clearFilters: 'مسح المرشحات',
    quickActions: 'إجراءات سريعة',
    createRequest: 'إنشاء طلب',
    customersPage: 'العملاء',
    requestsPage: 'الطلبات',
    dealsPage: 'الفواتير',
    inventoryPage: 'المخزون',
    usersPage: 'فريق المبيعات',
    totalNet: 'صافي المبيعات',
    companySales: 'مبيعات الشركة',
    companySalesText: 'قيمة المبيعات والكميات والتحصيل والمتبقي حسب المرشحات المختارة.',
    calculationsTitle: 'طريقة حساب الأرقام',
    grossFormula: 'إجمالي المبيعات = مجموع بنود الطلبات المختارة قبل الخصومات المعتمدة.',
    netFormula: 'صافي المبيعات = إجمالي المبيعات ناقص الخصومات المعتمدة فقط.',
    collectedFormula: 'الدخل المحصل = مجموع المبالغ المدفوعة المسجلة على الفواتير المفلترة.',
    outstandingFormula: 'المتبقي = قيمة الفاتورة المستحقة ناقص المبلغ المدفوع.',
    activityFormula: 'حالة العميل = نشط إذا كان لديه طلب واحد على الأقل خلال آخر 6 أشهر.',
    grossSales: 'إجمالي المبيعات',
    collected: 'الدخل المحصل',
    totalQuantity: 'الكمية',
    requestCount: 'الطلبات',
    pendingDiscounts: 'خصومات معلقة',
    overdueInvoices: 'فواتير متأخرة',
    outstanding: 'المتبقي',
    activeCustomers: 'العملاء',
    activeSalespeople: 'مستخدمو المبيعات',
    discountQueue: 'اعتماد الخصومات',
    dealsInvoices: 'الصفقات / الفواتير',
    customers: 'العملاء',
    teamReports: 'تقارير الفريق',
    productReports: 'تقارير المنتجات',
    inventory: 'المخزون',
    noData: 'لا توجد بيانات للمرشحات الحالية.',
    customer: 'العميل',
    customerId: 'رقم العميل',
    organization: 'المؤسسة',
    phone: 'الهاتف',
    area: 'المنطقة',
    assignedTo: 'مسؤول المبيعات',
    action: 'الإجراء',
    date: 'التاريخ',
    request: 'الطلب',
    subtotal: 'الإجمالي',
    requestedDiscount: 'الخصم المطلوب',
    discountAmount: 'قيمة الخصم',
    totalIfApproved: 'الإجمالي عند الاعتماد',
    currentTotal: 'الإجمالي الحالي',
    approve: 'اعتماد',
    reject: 'رفض',
    pending: 'قيد الانتظار',
    accepted: 'مقبول',
    rejected: 'مرفوض',
    held: 'معلق',
    cancelled: 'ملغى',
    dateChanged: 'تغيير تاريخ',
    notRequested: 'بدون خصم',
    approved: 'معتمد',
    cash: 'كاش',
    postpaid: 'آجل',
    unpaid: 'غير مدفوع',
    partiallyPaid: 'مدفوع جزئيا',
    paid: 'مدفوع',
    salesUser: 'مندوب المبيعات',
    dealDate: 'تاريخ الصفقة',
    products: 'المنتجات',
    quantity: 'الكمية',
    beforeTax: 'بدون ضريبة',
    tax: 'الضريبة',
    withTax: 'شامل الضريبة',
    taxMode: 'وضع الضريبة',
    paidAmount: 'المدفوع',
    remaining: 'المتبقي',
    dueDate: 'تاريخ الاستحقاق',
    view: 'عرض',
    showDetails: 'عرض التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'إجمالي السطر',
    sort: 'الصنف',
    color: 'اللون',
    paymentHistory: 'سجل الدفع',
    noPayments: 'لا توجد مدفوعات.',
    method: 'الطريقة',
    notes: 'ملاحظات',
    requests: 'الطلبات',
    fulfilled: 'منجز',
    discount: 'الخصم',
    customersServed: 'العملاء',
    lastActivity: 'آخر نشاط',
    sku: 'SKU',
    salespeople: 'المندوبون',
    avgQty: 'متوسط الكمية',
    stock: 'الرصيد',
    price: 'السعر',
    totalStock: 'إجمالي الرصيد',
    inventoryValue: 'قيمة المخزون',
    status: 'الحالة',
    viewProfile: 'عرض الملف',
    activeInPeriod: 'نشط',
    inactiveInPeriod: 'غير نشط',
    unassigned: 'غير معين',
    notAvailable: '-',
  },
};

const requestStatusKeys = ['pending', 'accepted', 'rejected', 'date-changed', 'held', 'cancelled'];
const paymentStatusKeys = ['unpaid', 'partially-paid', 'paid'];
const sectionKeys = ['discounts', 'deals', 'customers', 'team', 'products', 'inventory'];

const formatDateInput = (date) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return value.toISOString().slice(0, 10);
};

const getPeriodRange = (period) => {
  const now = new Date();
  const start = new Date(now);

  if (period === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (period === 'week') {
    start.setDate(now.getDate() - 6);
  } else if (period === 'year') {
    start.setMonth(0, 1);
  } else {
    start.setDate(now.getDate() - 30);
  }

  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(now),
  };
};

const getValueDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const sameId = (left, right) => String(left || '') === String(right || '');

const getRequestCustomerName = (request) =>
  request.customer?.fullName || request.customerSnapshot?.fullName || '';

const getRequestCustomerId = (request) =>
  request.customer?.customerID || request.customerSnapshot?.customerID || '';

const getRequestSubtotal = (request) =>
  Number(request.orderSummary?.subtotal) ||
  (Array.isArray(request.items)
    ? request.items.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0)
    : 0);

const getApprovedRequestTotal = (request) =>
  request.discountApprovalStatus === 'approved'
    ? Number(request.orderSummary?.totalAfterDiscount) || getRequestSubtotal(request)
    : getRequestSubtotal(request);

const getDealDate = (deal) => deal?.dealDate || deal?.createdAt;

const getDealLineTotal = (product) => {
  const quantity = Number(product?.quantity) || 0;
  const unitPrice = Number(product?.pricePerQuantity) > 0
    ? Number(product.pricePerQuantity)
    : Number(product?.pricePerItem) || 0;
  const discount = Math.min(100, Math.max(0, Number(product?.discount) || 0));
  const gross = quantity * unitPrice;
  return gross - gross * (discount / 100);
};

const getDealAmountDue = (deal) => {
  if (!deal) return 0;
  if (Number(deal.amountBeforeTax) > 0 || Number(deal.amountAfterTax) > 0) {
    return deal.taxInclusive ? Number(deal.amountAfterTax) || 0 : Number(deal.amountBeforeTax) || 0;
  }
  const subtotal = Array.isArray(deal.products)
    ? deal.products.reduce((sum, product) => sum + getDealLineTotal(product), 0)
    : 0;
  const discount = Math.min(100, Math.max(0, Number(deal.dealDiscount) || 0));
  return subtotal - subtotal * (discount / 100);
};

const isBetweenDates = (value, startDate, endDate) => {
  const date = getValueDate(value);
  if (!date) return false;
  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (date < start) return false;
  }
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (date > end) return false;
  }
  return true;
};

const SalesManagerDashboardView = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = usePersistentLanguage();
  const t = useCallback(
    (key) => translations[language][key] || translations.en[key] || key,
    [language]
  );
  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  const defaultRange = useMemo(() => getPeriodRange('month'), []);
  const [filters, setFilters] = useState({
    period: 'month',
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    salesUserId: 'all',
    productSKU: 'all',
    customerSearch: '',
    direction: 'all',
    requestStatus: 'all',
    paymentStatus: 'all',
  });

  const [activeSection, setActiveSection] = useState('discounts');
  const [expandedDeals, setExpandedDeals] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [deals, setDeals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [salesTeam, setSalesTeam] = useState([]);

  const formatNumber = useCallback(
    (value, maximumFractionDigits = 0) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return t('notAvailable');
      return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(numericValue);
    },
    [locale, t]
  );

  const formatCurrency = useCallback(
    (value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return t('notAvailable');
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EGP',
        maximumFractionDigits: 2,
      }).format(numericValue);
    },
    [locale, t]
  );

  const formatDate = useCallback(
    (value) => {
      return formatDateDDMMYYYY(value, t('notAvailable'));
    },
    [t]
  );

  const statusLabel = (status) => {
    const map = {
      pending: t('pending'),
      accepted: t('accepted'),
      rejected: t('rejected'),
      held: t('held'),
      cancelled: t('cancelled'),
      'date-changed': t('dateChanged'),
    };
    return map[status] || status || t('notAvailable');
  };

  const discountLabel = (status) => {
    const map = {
      pending: t('pending'),
      approved: t('approved'),
      rejected: t('rejected'),
      'not-requested': t('notRequested'),
    };
    return map[status] || t('notRequested');
  };

  const paymentLabel = (status) => {
    const map = {
      unpaid: t('unpaid'),
      'partially-paid': t('partiallyPaid'),
      paid: t('paid'),
    };
    return map[status] || status || t('notAvailable');
  };

  const actionLabel = (action) => {
    if (action === 'add') return t('inbound');
    if (action === 'remove') return t('outbound');
    return action || t('notAvailable');
  };

  const productBySku = useMemo(() => {
    const map = new Map();
    productCatalog.forEach((product) => map.set(product.SKU, product));
    return map;
  }, [productCatalog]);

  const salesOptions = useMemo(() => {
    const map = new Map();
    salesTeam
      .filter((user) => user.userType === 'sales' && user.isActive !== false)
      .forEach((user) => map.set(user._id, user.username || user._id));
    requests.forEach((request) => {
      if (request.salesUser?._id) {
        map.set(request.salesUser._id, request.salesUser.username || request.salesUser._id);
      }
    });
    customers.forEach((customer) => {
      if (customer.salesuser?._id) {
        map.set(customer.salesuser._id, customer.salesuser.username || customer.salesuser._id);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [customers, locale, requests, salesTeam]);

  const productOptions = useMemo(() => {
    const map = new Map();
    productCatalog.forEach((product) => {
      map.set(product.SKU, product.name ? `${product.SKU} - ${product.name}` : product.SKU);
    });
    requests.forEach((request) => {
      (request.items || []).forEach((item) => {
        if (!map.has(item.product)) map.set(item.product, item.product);
      });
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [locale, productCatalog, requests]);

  const buildReportParams = useCallback(() => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.salesUserId !== 'all') params.salesUserId = filters.salesUserId;
    if (filters.productSKU !== 'all') params.productSKU = filters.productSKU;
    if (filters.direction === 'inbound') params.action = 'add';
    if (filters.direction === 'outbound') params.action = 'remove';
    if (filters.requestStatus !== 'all') params.status = filters.requestStatus;
    return params;
  }, [filters]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashboardResponse, requestsResponse, dealsResponse, customersResponse, productsResponse, usersResponse] =
        await Promise.all([
          getSalesDashboard(buildReportParams()),
          getAllSalesRequests(),
          getAllDeals(),
          getCustomers(),
          getProducts(),
          getUsers({ roles: 'sales,sales-manager' }),
        ]);

      setDashboard(dashboardResponse.data || null);
      setRequests(Array.isArray(requestsResponse.data) ? requestsResponse.data : []);
      setDeals(Array.isArray(dealsResponse.data) ? dealsResponse.data : []);
      setCustomers(Array.isArray(customersResponse.data) ? customersResponse.data : []);
      setProductCatalog(Array.isArray(productsResponse.data) ? productsResponse.data : []);
      setSalesTeam(Array.isArray(usersResponse.data) ? usersResponse.data : []);
    } catch (loadError) {
      console.error('Error loading sales manager dashboard:', loadError);
      setError(t('failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [buildReportParams, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updateFilter = (field, value) => {
    setFilters((prev) => {
      if (field === 'period') {
        if (value === 'custom') {
          return { ...prev, period: value };
        }
        return { ...prev, period: value, ...getPeriodRange(value) };
      }
      const next = { ...prev, [field]: value };
      if (field === 'startDate' || field === 'endDate') {
        next.period = 'custom';
      }
      return next;
    });
  };

  const clearFilters = () => {
    const range = getPeriodRange('month');
    setFilters({
      period: 'month',
      startDate: range.startDate,
      endDate: range.endDate,
      salesUserId: 'all',
      productSKU: 'all',
      customerSearch: '',
      direction: 'all',
      requestStatus: 'all',
      paymentStatus: 'all',
    });
  };

  const matchesCustomerSearch = useCallback(
    (...values) => {
      const query = normalizeText(filters.customerSearch);
      if (!query) return true;
      return values.some((value) => normalizeText(value).includes(query));
    },
    [filters.customerSearch]
  );

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      if (!isBetweenDates(request.createdAt, filters.startDate, filters.endDate)) return false;
      if (filters.salesUserId !== 'all' && !sameId(request.salesUser?._id, filters.salesUserId)) return false;
      if (filters.productSKU !== 'all' && !(request.items || []).some((item) => item.product === filters.productSKU)) return false;
      if (filters.direction === 'inbound' && request.action !== 'add') return false;
      if (filters.direction === 'outbound' && request.action !== 'remove') return false;
      if (filters.requestStatus !== 'all' && request.status !== filters.requestStatus) return false;
      return matchesCustomerSearch(
        getRequestCustomerName(request),
        getRequestCustomerId(request),
        request.salesUser?.username,
        request._id
      );
    });
  }, [filters, matchesCustomerSearch, requests]);

  const filteredDeals = useMemo(() => {
    return deals.filter((row) => {
      const deal = row.deal || {};
      if (!isBetweenDates(getDealDate(deal), filters.startDate, filters.endDate)) return false;
      if (filters.salesUserId !== 'all' && !sameId(row.salesUserId, filters.salesUserId)) return false;
      if (filters.productSKU !== 'all' && !(deal.products || []).some((product) => product.SKU === filters.productSKU)) return false;
      if (filters.direction === 'inbound') return false;
      if (filters.paymentStatus !== 'all' && deal.paymentStatus !== filters.paymentStatus) return false;
      return matchesCustomerSearch(row.customerName, row.customerID, row.salesUserName);
    });
  }, [deals, filters, matchesCustomerSearch]);

  const customerActivityById = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 6);
    cutoffDate.setHours(0, 0, 0, 0);

    const activityMap = new Map();

    requests.forEach((request) => {
      const customerId = request.customer?._id || request.customer;
      const activityDate = getValueDate(request.createdAt);
      if (!customerId || !activityDate) return;

      const key = String(customerId);
      const current = activityMap.get(key);
      if (!current || activityDate > current.lastRequestAt) {
        activityMap.set(key, {
          lastRequestAt: activityDate,
          activeLastSixMonths: activityDate >= cutoffDate,
        });
      }
    });

    return activityMap;
  }, [requests]);

  const filteredCustomers = useMemo(() => {
    const activityByCustomer = new Map(
      (dashboard?.customerPerformance || []).map((row) => [String(row.customerId), row])
    );

    return customers
      .filter((customer) => {
        if (filters.salesUserId !== 'all' && !sameId(customer.salesuser?._id || customer.salesuser, filters.salesUserId)) {
          return false;
        }
        return matchesCustomerSearch(
          customer.fullName,
          customer.customerID,
          customer.organizationName,
          customer.phoneNumber,
          customer.governorate,
          customer.mainArea,
          customer.subArea,
          customer.salesuser?.username
        );
      })
      .map((customer) => ({
        ...customer,
        report: activityByCustomer.get(String(customer._id)) || {},
        activity: customerActivityById.get(String(customer._id)) || {
          lastRequestAt: null,
          activeLastSixMonths: false,
        },
      }));
  }, [customerActivityById, customers, dashboard, filters.salesUserId, matchesCustomerSearch]);

  const inventoryRows = useMemo(() => {
    const rows = [];
    productCatalog.forEach((product) => {
      (product.sorts || []).forEach((sort) => {
        (sort.colors || []).forEach((color) => {
          rows.push({
            productId: product._id,
            name: product.name,
            SKU: product.SKU,
            sort: sort.sortName,
            color: color.color,
            quantity: Number(color.quantity) || 0,
            price: Number(sort.price) || 0,
          });
        });
      });
    });
    return rows.filter((row) => {
      if (filters.productSKU !== 'all' && row.SKU !== filters.productSKU) return false;
      return matchesCustomerSearch(row.name, row.SKU, row.sort, row.color);
    });
  }, [filters.productSKU, matchesCustomerSearch, productCatalog]);

  const productPerformance = dashboard?.productPerformance || [];
  const salesPerformance = dashboard?.salesPerformance || [];
  const summary = dashboard?.summary || {};

  const pendingDiscountRequests = filteredRequests.filter(
    (request) => request.discountApprovalStatus === 'pending'
  );

  const overdueDeals = filteredDeals.filter((row) => {
    const deal = row.deal || {};
    if (deal.paymentType !== 'postpaid' || deal.paymentStatus === 'paid') return false;
    const dueDate = getValueDate(deal.paymentDueDate);
    return dueDate && dueDate < new Date();
  });

  const outstandingValue = filteredDeals.reduce((sum, row) => {
    const deal = row.deal || {};
    return sum + Math.max(0, getDealAmountDue(deal) - (Number(deal.paidAmount) || 0));
  }, 0);
  const collectedValue = filteredDeals.reduce(
    (sum, row) => sum + (Number(row.deal?.paidAmount) || 0),
    0
  );

  const totalInventoryQuantity = inventoryRows.reduce((sum, row) => sum + row.quantity, 0);
  const totalInventoryValue = inventoryRows.reduce(
    (sum, row) => sum + row.quantity * row.price,
    0
  );

  const sectionCounts = {
    discounts: pendingDiscountRequests.length,
    deals: filteredDeals.length,
    customers: filteredCustomers.length,
    team: salesPerformance.length,
    products: productPerformance.length,
    inventory: inventoryRows.length,
  };

  const overviewCards = [
    {
      label: t('totalNet'),
      value: formatCurrency(summary.totalNetValue),
      meta: `${t('grossSales')}: ${formatCurrency(summary.totalGrossValue)}`,
    },
    {
      label: t('totalQuantity'),
      value: formatNumber(summary.totalQuantity),
      meta: `${t('outbound')}: ${formatNumber(summary.outboundQuantity)}`,
    },
    {
      label: t('pendingDiscounts'),
      value: formatNumber(pendingDiscountRequests.length),
      meta: `${t('discount')}: ${formatCurrency(summary.totalDiscountValue)}`,
    },
    {
      label: t('overdueInvoices'),
      value: formatNumber(overdueDeals.length),
      meta: `${t('outstanding')}: ${formatCurrency(outstandingValue)}`,
    },
    {
      label: t('activeCustomers'),
      value: formatNumber(filteredCustomers.length),
      meta: `${t('activeSalespeople')}: ${formatNumber(salesOptions.length)}`,
    },
    {
      label: t('inventory'),
      value: formatNumber(totalInventoryQuantity),
      meta: `${t('inventoryValue')}: ${formatCurrency(totalInventoryValue)}`,
    },
  ];

  const handleDiscountDecision = async (requestId, action) => {
    setActionLoading(`${requestId}-${action}`);
    setError('');
    setMessage('');
    try {
      const response = await approveDiscount(requestId, action);
      setRequests((prev) =>
        prev.map((request) => (request._id === requestId ? response.data : request))
      );
      setMessage(t('discountUpdated'));
      await loadDashboard();
    } catch (decisionError) {
      console.error('Error updating discount decision:', decisionError);
      setError(decisionError?.response?.data?.message || t('failedDiscountUpdate'));
    } finally {
      setActionLoading('');
    }
  };

  const toggleDealDetails = (dealId) => {
    setExpandedDeals((prev) => ({ ...prev, [dealId]: !prev[dealId] }));
  };

  const renderFilterBar = () => (
    <section className={styles.filtersPanel}>
      <label className={styles.filterField}>
        <span>{t('period')}</span>
        <select value={filters.period} onChange={(event) => updateFilter('period', event.target.value)}>
          <option value="today">{t('today')}</option>
          <option value="week">{t('week')}</option>
          <option value="month">{t('month')}</option>
          <option value="year">{t('year')}</option>
          <option value="custom">{t('custom')}</option>
        </select>
      </label>
      <label className={styles.filterField}>
        <span>{t('startDate')}</span>
        <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
      </label>
      <label className={styles.filterField}>
        <span>{t('endDate')}</span>
        <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
      </label>
      <label className={styles.filterField}>
        <span>{t('salesperson')}</span>
        <select value={filters.salesUserId} onChange={(event) => updateFilter('salesUserId', event.target.value)}>
          <option value="all">{t('allSalespeople')}</option>
          {salesOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label className={styles.filterField}>
        <span>{t('product')}</span>
        <select value={filters.productSKU} onChange={(event) => updateFilter('productSKU', event.target.value)}>
          <option value="all">{t('allProducts')}</option>
          {productOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label className={styles.filterField}>
        <span>{t('direction')}</span>
        <select value={filters.direction} onChange={(event) => updateFilter('direction', event.target.value)}>
          <option value="all">{t('allDirections')}</option>
          <option value="inbound">{t('inbound')}</option>
          <option value="outbound">{t('outbound')}</option>
        </select>
      </label>
      <label className={styles.filterField}>
        <span>{t('requestStatus')}</span>
        <select value={filters.requestStatus} onChange={(event) => updateFilter('requestStatus', event.target.value)}>
          <option value="all">{t('allStatuses')}</option>
          {requestStatusKeys.map((status) => (
            <option key={status} value={status}>{statusLabel(status)}</option>
          ))}
        </select>
      </label>
      <label className={styles.filterField}>
        <span>{t('paymentStatus')}</span>
        <select value={filters.paymentStatus} onChange={(event) => updateFilter('paymentStatus', event.target.value)}>
          <option value="all">{t('allPaymentStatuses')}</option>
          {paymentStatusKeys.map((status) => (
            <option key={status} value={status}>{paymentLabel(status)}</option>
          ))}
        </select>
      </label>
      <label className={`${styles.filterField} ${styles.searchField}`}>
        <span>{t('customerSearch')}</span>
        <input
          type="search"
          value={filters.customerSearch}
          onChange={(event) => updateFilter('customerSearch', event.target.value)}
          placeholder={t('customerSearchPlaceholder')}
        />
      </label>
      <button type="button" className={styles.clearButton} onClick={clearFilters}>
        {t('clearFilters')}
      </button>
    </section>
  );

  const renderOverview = () => (
    <section className={styles.overviewGrid}>
      {overviewCards.map((card) => (
        <article key={card.label} className={styles.overviewCard}>
          <span className={styles.cardLabel}>{card.label}</span>
          <strong className={styles.cardValue}>{card.value}</strong>
          <span className={styles.cardMeta}>{card.meta}</span>
        </article>
      ))}
    </section>
  );

  const renderCompanySnapshot = () => (
    <section className={styles.companyPanel}>
      <div className={styles.companyPanelIntro}>
        <span>{t('companySales')}</span>
        <p>{t('companySalesText')}</p>
      </div>
      <div className={styles.companyMetrics}>
        <div>
          <span>{t('grossSales')}</span>
          <strong>{formatCurrency(summary.totalGrossValue)}</strong>
        </div>
        <div>
          <span>{t('totalNet')}</span>
          <strong>{formatCurrency(summary.totalNetValue)}</strong>
        </div>
        <div>
          <span>{t('collected')}</span>
          <strong>{formatCurrency(collectedValue)}</strong>
        </div>
        <div>
          <span>{t('outstanding')}</span>
          <strong>{formatCurrency(outstandingValue)}</strong>
        </div>
        <div>
          <span>{t('totalQuantity')}</span>
          <strong>{formatNumber(summary.totalQuantity)}</strong>
        </div>
        <div>
          <span>{t('requestCount')}</span>
          <strong>{formatNumber(summary.totalRequests)}</strong>
        </div>
      </div>
      <div className={styles.calculationInfo}>
        <strong>{t('calculationsTitle')}</strong>
        <ul>
          <li>{t('grossFormula')}</li>
          <li>{t('netFormula')}</li>
          <li>{t('collectedFormula')}</li>
          <li>{t('outstandingFormula')}</li>
          <li>{t('activityFormula')}</li>
        </ul>
      </div>
    </section>
  );

  const renderSectionNav = () => (
    <nav className={styles.sectionNav} aria-label={t('pageTitle')}>
      {sectionKeys.map((section) => (
        <button
          key={section}
          type="button"
          className={`${styles.sectionTab} ${activeSection === section ? styles.sectionTabActive : ''}`}
          onClick={() => setActiveSection(section)}
        >
          <span>{t(section === 'discounts' ? 'discountQueue' : section === 'deals' ? 'dealsInvoices' : section === 'team' ? 'teamReports' : section === 'products' ? 'productReports' : section)}</span>
          <strong>{formatNumber(sectionCounts[section])}</strong>
        </button>
      ))}
    </nav>
  );

  const renderDiscountQueue = () => (
    <section className={styles.tablePanel}>
      <div className={styles.tableHeader}>
        <h2>{t('discountQueue')}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('customer')}</th>
              <th>{t('salesUser')}</th>
              <th>{t('date')}</th>
              <th>{t('action')}</th>
              <th>{t('subtotal')}</th>
              <th>{t('requestedDiscount')}</th>
              <th>{t('discountAmount')}</th>
              <th>{t('totalIfApproved')}</th>
              <th>{t('currentTotal')}</th>
              <th>{t('status')}</th>
              <th>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {pendingDiscountRequests.length === 0 ? (
              <tr><td colSpan={12} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              pendingDiscountRequests.map((request, index) => (
                <tr key={request._id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className={styles.stackedCell}>
                      <strong>{getRequestCustomerName(request) || t('notAvailable')}</strong>
                      <span>{getRequestCustomerId(request)}</span>
                    </div>
                  </td>
                  <td>{request.salesUser?.username || t('notAvailable')}</td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td><span className={styles.badge}>{actionLabel(request.action)}</span></td>
                  <td>{formatCurrency(getRequestSubtotal(request))}</td>
                  <td>{formatNumber(request.requestedDiscountPercent, 2)}%</td>
                  <td>{formatCurrency(request.orderSummary?.discountAmount)}</td>
                  <td>{formatCurrency(request.orderSummary?.totalAfterDiscount)}</td>
                  <td>{formatCurrency(getApprovedRequestTotal(request))}</td>
                  <td><span className={`${styles.statusPill} ${styles.statusPending}`}>{discountLabel(request.discountApprovalStatus)}</span></td>
                  <td>
                    <div className={styles.actionsCell}>
                      <button
                        type="button"
                        className={styles.approveButton}
                        onClick={() => handleDiscountDecision(request._id, 'approve')}
                        disabled={Boolean(actionLoading)}
                      >
                        {actionLoading === `${request._id}-approve` ? '...' : t('approve')}
                      </button>
                      <button
                        type="button"
                        className={styles.rejectButton}
                        onClick={() => handleDiscountDecision(request._id, 'reject')}
                        disabled={Boolean(actionLoading)}
                      >
                        {actionLoading === `${request._id}-reject` ? '...' : t('reject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderDealDetails = (row) => {
    const deal = row.deal || {};
    const amountDue = getDealAmountDue(deal);
    const remaining = Math.max(0, amountDue - (Number(deal.paidAmount) || 0));
    return (
      <tr className={styles.expandedRow}>
        <td colSpan={13}>
          <div className={styles.detailsPanel}>
            <div className={styles.detailSummary}>
              <div><span>{t('subtotal')}</span><strong>{formatCurrency(deal.subtotal)}</strong></div>
              <div><span>{t('discount')}</span><strong>{formatCurrency(deal.discountAmount)}</strong></div>
              <div><span>{t('tax')}</span><strong>{formatCurrency(deal.taxAmount)} ({formatNumber(deal.taxRateSnapshot, 2)}%)</strong></div>
              <div><span>{t('currentTotal')}</span><strong>{formatCurrency(amountDue)}</strong></div>
              <div><span>{t('paidAmount')}</span><strong>{formatCurrency(deal.paidAmount || 0)}</strong></div>
              <div><span>{t('remaining')}</span><strong>{formatCurrency(remaining)}</strong></div>
            </div>
            <div className={styles.subTableBlock}>
              <h3>{t('products')}</h3>
              <table className={styles.subTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('product')}</th>
                    <th>{t('sku')}</th>
                    <th>{t('sort')}</th>
                    <th>{t('color')}</th>
                    <th>{t('quantity')}</th>
                    <th>{t('unitPrice')}</th>
                    <th>{t('lineTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(deal.products || []).map((product, index) => (
                    <tr key={`${deal._id}-product-${index}`}>
                      <td>{index + 1}</td>
                      <td>{product.productName || product.SKU}</td>
                      <td>{product.SKU}</td>
                      <td>{product.sort || t('notAvailable')}</td>
                      <td>{product.color || t('notAvailable')}</td>
                      <td>{formatNumber(product.quantity)}</td>
                      <td>{formatCurrency(product.pricePerItem)}</td>
                      <td>{formatCurrency(getDealLineTotal(product))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.subTableBlock}>
              <h3>{t('paymentHistory')}</h3>
              {!deal.payments?.length ? (
                <p className={styles.emptyText}>{t('noPayments')}</p>
              ) : (
                <table className={styles.subTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('date')}</th>
                      <th>{t('paidAmount')}</th>
                      <th>{t('method')}</th>
                      <th>{t('notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.payments.map((payment, index) => (
                      <tr key={`${deal._id}-payment-${index}`}>
                        <td>{index + 1}</td>
                        <td>{formatDate(payment.paidAt)}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>{payment.method || t('notAvailable')}</td>
                        <td>{payment.notes || t('notAvailable')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderDealsTable = () => (
    <section className={styles.tablePanel}>
      <div className={styles.tableHeader}>
        <h2>{t('dealsInvoices')}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('customer')}</th>
              <th>{t('salesUser')}</th>
              <th>{t('dealDate')}</th>
              <th>{t('products')}</th>
              <th>{t('quantity')}</th>
              <th>{t('beforeTax')}</th>
              <th>{t('withTax')}</th>
              <th>{t('taxMode')}</th>
              <th>{t('paidAmount')}</th>
              <th>{t('remaining')}</th>
              <th>{t('paymentStatus')}</th>
              <th>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.length === 0 ? (
              <tr><td colSpan={13} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              filteredDeals.map((row, index) => {
                const deal = row.deal || {};
                const dealId = String(deal._id);
                const amountDue = getDealAmountDue(deal);
                const remaining = Math.max(0, amountDue - (Number(deal.paidAmount) || 0));
                const quantity = (deal.products || []).reduce((sum, product) => sum + (Number(product.quantity) || 0), 0);
                const expanded = Boolean(expandedDeals[dealId]);
                return (
                  <React.Fragment key={dealId}>
                    <tr>
                      <td>{index + 1}</td>
                      <td>
                        <div className={styles.stackedCell}>
                          <strong>{row.customerName || t('notAvailable')}</strong>
                          <span>{row.customerID}</span>
                        </div>
                      </td>
                      <td>{row.salesUserName || t('notAvailable')}</td>
                      <td>{formatDate(getDealDate(deal))}</td>
                      <td>{formatNumber((deal.products || []).length)}</td>
                      <td>{formatNumber(quantity)}</td>
                      <td>{formatCurrency(deal.amountBeforeTax || deal.subtotal)}</td>
                      <td>{formatCurrency(deal.amountAfterTax || getDealAmountDue(deal))}</td>
                      <td>{deal.taxInclusive ? t('withTax') : t('beforeTax')}</td>
                      <td className={styles.positiveText}>{formatCurrency(deal.paidAmount || 0)}</td>
                      <td className={remaining > 0 ? styles.dangerText : styles.positiveText}>{formatCurrency(remaining)}</td>
                      <td><span className={`${styles.statusPill} ${styles[`payment-${deal.paymentStatus || 'unpaid'}`]}`}>{paymentLabel(deal.paymentStatus || 'unpaid')}</span></td>
                      <td>
                        <div className={styles.actionsCell}>
                          <button type="button" className={styles.textButton} onClick={() => toggleDealDetails(dealId)}>
                            {expanded ? t('hideDetails') : t('showDetails')}
                          </button>
                          <button type="button" className={styles.textButton} onClick={() => navigate(`/customers/${row.customerId}`)}>
                            {t('view')}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded && renderDealDetails(row)}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderCustomersTable = () => (
    <section className={styles.tablePanel}>
      <div className={styles.tableHeader}>
        <h2>{t('customers')}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('customer')}</th>
              <th>{t('organization')}</th>
              <th>{t('phone')}</th>
              <th>{t('area')}</th>
              <th>{t('assignedTo')}</th>
              <th>{t('requests')}</th>
              <th>{t('quantity')}</th>
              <th>{t('totalNet')}</th>
              <th>{t('lastActivity')}</th>
              <th>{t('status')}</th>
              <th>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr><td colSpan={12} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              filteredCustomers.map((customer, index) => {
                const activeLastSixMonths = Boolean(customer.activity.activeLastSixMonths);
                return (
                  <tr key={customer._id} className={!activeLastSixMonths ? styles.inactiveCustomerRow : undefined}>
                    <td>{index + 1}</td>
                    <td>
                      <div className={styles.stackedCell}>
                        <strong>{customer.fullName}</strong>
                        <span>{customer.customerID}</span>
                      </div>
                    </td>
                    <td>{customer.organizationName || t('notAvailable')}</td>
                    <td>{customer.phoneNumber || t('notAvailable')}</td>
                    <td>{[customer.governorate, customer.mainArea, customer.subArea].filter(Boolean).join(' / ') || t('notAvailable')}</td>
                    <td>{customer.salesuser?.username || t('unassigned')}</td>
                    <td>{formatNumber(customer.report.requestCount || 0)}</td>
                    <td>{formatNumber(customer.report.totalQuantity || 0)}</td>
                    <td>{formatCurrency(customer.report.totalNetValue || 0)}</td>
                    <td>{formatDate(customer.activity.lastRequestAt)}</td>
                    <td>
                      <span
                        className={`${styles.activityPill} ${activeLastSixMonths ? styles.activityActive : styles.activityInactive}`}
                        title={t('activityFormula')}
                      >
                        {activeLastSixMonths ? t('activeInPeriod') : t('inactiveInPeriod')}
                      </span>
                    </td>
                    <td>
                      <button type="button" className={styles.textButton} onClick={() => navigate(`/customers/${customer._id}`)}>
                        {t('viewProfile')}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderTeamTable = () => (
    <section className={styles.tablePanel}>
      <div className={styles.tableHeader}>
        <h2>{t('teamReports')}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('salesUser')}</th>
              <th>{t('requests')}</th>
              <th>{t('fulfilled')}</th>
              <th>{t('quantity')}</th>
              <th>{t('grossSales')}</th>
              <th>{t('discount')}</th>
              <th>{t('totalNet')}</th>
              <th>{t('customersServed')}</th>
              <th>{t('products')}</th>
              <th>{t('lastActivity')}</th>
            </tr>
          </thead>
          <tbody>
            {salesPerformance.length === 0 ? (
              <tr><td colSpan={11} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              salesPerformance.map((row, index) => (
                <tr key={row.salesUserId}>
                  <td>{index + 1}</td>
                  <td>{row.salesUserName || t('notAvailable')}</td>
                  <td>{formatNumber(row.requestCount)}</td>
                  <td>{formatNumber(row.fulfilledCount)}</td>
                  <td>{formatNumber(row.totalQuantity)}</td>
                  <td>{formatCurrency(row.totalGrossValue)}</td>
                  <td>{formatCurrency(row.totalDiscountValue)}</td>
                  <td>{formatCurrency(row.totalNetValue)}</td>
                  <td>{formatNumber(row.customersServed)}</td>
                  <td>{formatNumber(row.productsHandled)}</td>
                  <td>{formatDate(row.lastRequestAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderProductsTable = () => (
    <section className={styles.tablePanel}>
      <div className={styles.tableHeader}>
        <h2>{t('productReports')}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('product')}</th>
              <th>{t('requests')}</th>
              <th>{t('quantity')}</th>
              <th>{t('grossSales')}</th>
              <th>{t('discount')}</th>
              <th>{t('totalNet')}</th>
              <th>{t('salespeople')}</th>
              <th>{t('customers')}</th>
              <th>{t('avgQty')}</th>
              <th>{t('lastActivity')}</th>
            </tr>
          </thead>
          <tbody>
            {productPerformance.length === 0 ? (
              <tr><td colSpan={11} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              productPerformance.map((row, index) => (
                <tr key={row.product}>
                  <td>{index + 1}</td>
                  <td>
                    <div className={styles.stackedCell}>
                      <strong>{row.productName || productBySku.get(row.product)?.name || row.product}</strong>
                      <span>{row.product}</span>
                    </div>
                  </td>
                  <td>{formatNumber(row.requestCount)}</td>
                  <td>{formatNumber(row.totalQuantity)}</td>
                  <td>{formatCurrency(row.totalGrossValue)}</td>
                  <td>{formatCurrency(row.totalDiscountValue)}</td>
                  <td>{formatCurrency(row.totalNetValue)}</td>
                  <td>{formatNumber(row.salespeopleInvolved)}</td>
                  <td>{formatNumber(row.customersInvolved)}</td>
                  <td>{formatNumber(row.averageQuantityPerRequest, 1)}</td>
                  <td>{formatDate(row.lastActivityAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderInventoryTable = () => (
    <section className={styles.tablePanel}>
      <div className={styles.tableHeader}>
        <h2>{t('inventory')}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('product')}</th>
              <th>{t('sku')}</th>
              <th>{t('sort')}</th>
              <th>{t('color')}</th>
              <th>{t('stock')}</th>
              <th>{t('price')}</th>
              <th>{t('totalStock')}</th>
            </tr>
          </thead>
          <tbody>
            {inventoryRows.length === 0 ? (
              <tr><td colSpan={8} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              inventoryRows.map((row, index) => (
                <tr key={`${row.SKU}-${row.sort}-${row.color}`}>
                  <td>{index + 1}</td>
                  <td>{row.name}</td>
                  <td>{row.SKU}</td>
                  <td>{row.sort}</td>
                  <td>{row.color}</td>
                  <td>{formatNumber(row.quantity)}</td>
                  <td>{formatCurrency(row.price)}</td>
                  <td>{formatCurrency(row.quantity * row.price)}</td>
                </tr>
              ))
            )}
          </tbody>
          {inventoryRows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5}>{t('totalStock')}</td>
                <td>{formatNumber(totalInventoryQuantity)}</td>
                <td></td>
                <td>{formatCurrency(totalInventoryValue)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );

  const renderActiveSection = () => {
    if (activeSection === 'discounts') return renderDiscountQueue();
    if (activeSection === 'deals') return renderDealsTable();
    if (activeSection === 'customers') return renderCustomersTable();
    if (activeSection === 'team') return renderTeamTable();
    if (activeSection === 'products') return renderProductsTable();
    return renderInventoryTable();
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      subtitle={t('subtitle')}
      language={language}
      languageLabel={language === 'en' ? t('arabic') : t('english')}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      showLogout
      logoutLabel={t('logout')}
      contentClassName={styles.salesManagerDashboard}
      contentProps={{ 'aria-busy': loading }}
    >
      <div className={styles.pageBody}>
        <section className={styles.toolbar}>
          <div className={styles.quickActions}>
            <span>{t('quickActions')}</span>
            <button type="button" onClick={() => navigate('/customers')}>{t('customersPage')}</button>
            <button type="button" onClick={() => navigate('/sales-create-request')}>{t('createRequest')}</button>
            <button type="button" onClick={() => navigate('/sales-requests-management')}>{t('requestsPage')}</button>
            <button type="button" onClick={() => navigate('/accounting/deals')}>{t('dealsPage')}</button>
            <button type="button" onClick={() => navigate('/products-inventory')}>{t('inventoryPage')}</button>
            <button type="button" onClick={() => navigate('/manage-users')}>{t('usersPage')}</button>
          </div>
        </section>

        {renderFilterBar()}

        {error && <div className={styles.errorBanner}>{error}</div>}
        {message && <div className={styles.successBanner}>{message}</div>}

        {renderOverview()}
        {renderCompanySnapshot()}
        {renderSectionNav()}
        {renderActiveSection()}
      </div>

      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner} />
          <p>{t('loading')}</p>
        </div>
      )}
    </AppLayout>
  );
};

export default SalesManagerDashboardView;
